import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Facebook, Instagram, Youtube, Linkedin, MessageCircle, Music2 } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { IMG, RECEPISSES } from "@/lib/content";
import { telHref } from "@/lib/api";
import { NewsletterForm } from "@/components/forms/NewsletterForm";

const SOCIAL_ICONS = { facebook: Facebook, whatsapp: MessageCircle, instagram: Instagram, youtube: Youtube, linkedin: Linkedin, tiktok: Music2 };

function StructureBlock({ name, logo, contact, receipt, color, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-3">
        <img src={logo} alt={`Logo ${name}`} loading="lazy" className="h-14 w-14 rounded-md bg-white object-contain p-1" />
        <p className="font-display text-lg font-bold uppercase tracking-wide" style={{ color }}>{name}</p>
      </div>
      <p className="mt-3 text-xs text-white/55">{receipt}</p>
      {contact && (
        <ul className="mt-3 space-y-1.5 text-sm text-white/80">
          <li className="flex gap-2"><MapPin className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />{contact.address}</li>
          <li><a href={`mailto:${contact.email}`} className="flex gap-2 hover:text-gold"><Mail className="h-4 w-4 mt-0.5" aria-hidden="true" />{contact.email}</a></li>
          {contact.phones?.map((p) => (
            <li key={p}><a href={telHref(p)} className="flex gap-2 hover:text-gold"><Phone className="h-4 w-4 mt-0.5" aria-hidden="true" />{p}</a></li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function Footer() {
  const { settings } = useSite();
  const socials = Object.entries(settings?.socials || {}).filter(([, v]) => v);
  return (
    <footer className="relative bg-ink text-white" data-testid="site-footer">
      <div className="absolute inset-0 pagne-light" />
      <div className="relative h-1.5 w-full bg-[linear-gradient(90deg,#1E7B34_0_33%,#E0A82E_33%_66%,#C8161D_66%)]" />
      <div className="container-x relative grid gap-12 py-14 md:grid-cols-2 lg:grid-cols-4">
        <StructureBlock name="ONG MA LEBA" logo={IMG.logoOng} contact={settings?.contacts?.ong} receipt={RECEPISSES.ong} color="#7fcf8f" testid="footer-ong" />
        <StructureBlock name="Association MA LEBA" logo={IMG.logoAsso} contact={settings?.contacts?.association} receipt={RECEPISSES.association} color="#ff7a7f" testid="footer-association" />
        <div>
          <p className="font-display text-lg font-bold uppercase tracking-wide text-gold">Liens</p>
          <ul className="mt-3 grid grid-cols-2 gap-2 text-sm text-white/80 lg:grid-cols-1">
            {[["/ma-leba", "Ma Leba"], ["/actualites", "Actualités"], ["/lettres", "Lettres d'information"], ["/galerie", "Galerie"], ["/devenir-partenaire", "Devenir partenaire"],
              ["/communication", "Espace communication"], ["/contact", "Contact"], ["/mentions-legales", "Mentions légales"], ["/confidentialite", "Confidentialité"]].map(([to, l]) => (
              <li key={to}><Link to={to} className="hover:text-gold" data-testid={`footer-link-${to.slice(1)}`}>{l}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <p className="font-display text-lg font-bold uppercase tracking-wide text-gold">Newsletter</p>
          <p className="mt-2 text-sm text-white/70">Recevez nos nouvelles.</p>
          <NewsletterForm compact />
          {socials.length > 0 && (
            <div className="mt-6 flex gap-2" data-testid="footer-socials">
              {socials.map(([k, url]) => {
                const Icon = SOCIAL_ICONS[k] || MessageCircle;
                return (
                  <a key={k} href={url} target="_blank" rel="noopener noreferrer" aria-label={k} data-testid={`social-${k}`}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 transition-colors hover:bg-gold hover:text-ink">
                    <Icon className="h-5 w-5" />
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <div className="relative border-t border-white/10">
        <div className="container-x flex flex-col gap-2 py-6 text-sm text-white/60 md:flex-row md:items-center md:justify-between">
          <p className="font-display text-base uppercase tracking-wide text-gold" data-testid="footer-slogan">Ensemble, chaque geste compte, chaque vie compte.</p>
          <p>© {new Date().getFullYear()} ONG MA LEBA & ASSOCIATION MA LEBA — Makana, République du Congo</p>
        </div>
      </div>
    </footer>
  );
}
